import React, { useEffect, useRef, useState } from 'react';
import { Globe, Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslations } from '../hooks/useTranslations';
import type { SupportedLanguage } from '../locales';
import { getArticleBySlug, getLocalizedBlogArticlePath } from '../blog/articles';
import { getCaptionsLandingNavLabel } from '../content/captionsLanding';
import { CREATORS_LANDING_PATH, getCreatorsLandingLabel } from '../content/creatorsLanding';
import {
  DUBBING_LANDING_PATH,
  FREE_CAPTIONS_LANDING_PATH,
  getBasePathFromUrlPath,
  getBlogArticleSlugFromUrlPath,
  getLocalizedPath,
  isDubbingPairPath,
  type LocalizedBasePath,
} from '../utils/localizedRoutes';
import './Header.css';

export function Header() {
  const navigate = useNavigate();
  const { t, currentLanguage, availableLanguages } = useTranslations();
  const location = useLocation();
  const headerRef = useRef<HTMLElement | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileMenuOffset, setMobileMenuOffset] = useState(0);
  const currentBasePath = getBasePathFromUrlPath(location.pathname);

  const languageNames: Record<string, string> = {
    en: 'English',
    ru: 'Русский',
    es: 'Español',
    fr: 'Français',
    de: 'Deutsch',
    it: 'Italiano',
    pt: 'Português',
    pl: 'Polski',
    ja: '日本語',
    ko: '한국어',
    zh: '中文',
    ar: 'العربية',
    bg: 'Български',
    hr: 'Hrvatski',
    cs: 'Čeština',
    da: 'Dansk',
    nl: 'Nederlands',
    et: 'Eesti',
    fi: 'Suomi',
    el: 'Ελληνικά',
    hu: 'Magyar',
    ga: 'Gaeilge',
    lv: 'Latviešu',
    lt: 'Lietuvių',
    mt: 'Malti',
    ro: 'Română',
    sk: 'Slovenčina',
    sl: 'Slovenščina',
    sv: 'Svenska',
  };

  const shortOfferLabels: Record<string, string> = {
    en: '$2 per Short',
    ru: '$2 за Short',
    es: '$2 por Short',
    fr: '2 $ par Short',
    de: '2 $ pro Short',
    it: '2 $ per Short',
    pt: '$2 por Short',
    pl: '$2 za Short',
    ja: 'Short 1本 2ドル',
    ko: 'Short당 $2',
    zh: '每条 Short 2 美元',
    ar: '2$ لكل Short',
    bg: '$2 за Short',
    hr: '2 $ po Shortu',
    cs: '2 $ za Short',
    da: '2 $ pr. Short',
    nl: '$2 per Short',
    et: '2 $ Shorti eest',
    fi: '2 $ / Short',
    el: '2 $ ανά Short',
    hu: '2 $ / Short',
    ga: '$2 an Short',
    lv: '2 $ par Short',
    lt: '2 $ už Short',
    mt: '$2 għal kull Short',
    ro: '2 $ per Short',
    sk: '2 $ za Short',
    sl: '2 $ na Short',
    sv: '2 $ per Short',
  };
  
  const isBlogPage = currentBasePath === '/blog';
  const isCreatorsLandingPage = currentBasePath === CREATORS_LANDING_PATH;
  const isCaptionsLandingPage = currentBasePath === FREE_CAPTIONS_LANDING_PATH;
  const isPairDubbingPage = Boolean(currentBasePath && isDubbingPairPath(currentBasePath));
  const isHomePage = currentBasePath === '/';
  const isDubbingActive = currentBasePath === DUBBING_LANDING_PATH || isPairDubbingPage;
  const shortOfferLabel =
    shortOfferLabels[currentLanguage] || shortOfferLabels.en;
  const creatorsLandingLabel = getCreatorsLandingLabel(currentLanguage);
  const captionsLandingLabel = getCaptionsLandingNavLabel(currentLanguage);

  const buildUrl = (path: LocalizedBasePath) => {
    const localizedPath = getLocalizedPath(path, currentLanguage);
    return localizedPath;
  };

  const navigationLinks = [
    {
      to: buildUrl("/"),
      label: shortOfferLabel,
      isActive: isHomePage,
    },
    {
      to: buildUrl(DUBBING_LANDING_PATH),
      label: t.services?.dubbing || 'Synchronisation',
      isActive: isDubbingActive,
    },
    {
      to: buildUrl("/blog"),
      label: 'Breaking News',
      isActive: isBlogPage,
    },
    {
      to: buildUrl(CREATORS_LANDING_PATH),
      label: creatorsLandingLabel,
      isActive: isCreatorsLandingPage,
    },
    {
      to: buildUrl(FREE_CAPTIONS_LANDING_PATH),
      label: captionsLandingLabel,
      isActive: isCaptionsLandingPage,
    },
  ];

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const updateMobileMenuOffset = () => {
      setMobileMenuOffset(headerRef.current?.getBoundingClientRect().height || 0);
    };

    updateMobileMenuOffset();
    window.addEventListener('resize', updateMobileMenuOffset);

    return () => {
      window.removeEventListener('resize', updateMobileMenuOffset);
    };
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMobileMenuOpen]);

  const handleLanguageChange = (newLanguage: string) => {
    if (newLanguage === currentLanguage) return;

    const targetLanguage = newLanguage as SupportedLanguage;
    const currentArticleSlug = getBlogArticleSlugFromUrlPath(location.pathname);

    if (currentArticleSlug) {
      const currentArticle = getArticleBySlug(currentArticleSlug, currentLanguage);
      if (currentArticle) {
        const localizedArticlePath =
          getLocalizedBlogArticlePath(currentArticle.id, targetLanguage);

        if (localizedArticlePath) {
          navigate(localizedArticlePath, { replace: true });
          return;
        }
      }
    }

    const basePath: LocalizedBasePath = currentBasePath || "/";
    const localizedPath = getLocalizedPath(basePath, targetLanguage);

    navigate(localizedPath, { replace: true });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header ref={headerRef} className="vevi-header">
      <div className="vevi-header-container">
        <div className="vevi-header-content">
          <Link to={buildUrl("/")} className="vevi-header-logo" style={{ textDecoration: 'none' }}>
            Vevi Dubbing Monster
          </Link>
          <nav className="vevi-header-nav">
            {navigationLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`vevi-header-nav-link ${link.isActive ? "vevi-header-nav-link-active" : ""}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="vevi-header-language">
            <Globe className="vevi-header-globe-icon" aria-hidden="true" />
            <label htmlFor="header-language-select" className="vevi-header-sr-only">
              Select language
            </label>
            <select
              id="header-language-select"
              className="vevi-header-select"
              value={currentLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              aria-label="Select language"
            >
              {availableLanguages.map((lang) => (
                <option key={lang} value={lang}>
                  {languageNames[lang] || lang.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="vevi-header-menu-button"
            aria-expanded={isMobileMenuOpen}
            aria-controls="vevi-mobile-navigation"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={toggleMobileMenu}
          >
            {isMobileMenuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>
      <div
        className={`vevi-header-mobile-overlay ${isMobileMenuOpen ? 'vevi-header-mobile-overlay-open' : ''}`}
        style={{ top: mobileMenuOffset || undefined }}
        onClick={closeMobileMenu}
        aria-hidden={!isMobileMenuOpen}
      />
      <div
        id="vevi-mobile-navigation"
        className={`vevi-header-mobile-panel ${isMobileMenuOpen ? 'vevi-header-mobile-panel-open' : ''}`}
        style={{ top: mobileMenuOffset || undefined }}
      >
        <div className="vevi-header-mobile-panel-inner">
          <nav className="vevi-header-mobile-nav" aria-label="Mobile">
            {navigationLinks.map((link) => (
              <Link
                key={`mobile-${link.to}`}
                to={link.to}
                className={`vevi-header-mobile-nav-link ${link.isActive ? 'vevi-header-mobile-nav-link-active' : ''}`}
                onClick={closeMobileMenu}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="vevi-header-mobile-language-section">
            <div className="vevi-header-mobile-language-label">
              <Globe className="vevi-header-globe-icon" aria-hidden="true" />
              <span>Language</span>
            </div>
            <label htmlFor="mobile-header-language-select" className="vevi-header-sr-only">
              Select language
            </label>
            <select
              id="mobile-header-language-select"
              className="vevi-header-mobile-select"
              value={currentLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              aria-label="Select language"
            >
              {availableLanguages.map((lang) => (
                <option key={`mobile-${lang}`} value={lang}>
                  {languageNames[lang] || lang.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </header>
  );
}
